import { Box, IconButton, Spinner, Text } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { emptyPaginationResponse } from '@duke-hq/lib/pagination';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { CourseReferralButton } from '@duke-hq/app-provider-web/tenant/components/CourseReferralModal';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { Plural, Trans } from '@lingui/react/macro';
import { ArrowRightFromLine } from 'lucide-react';
import { PendingReferralsTable } from './Table';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';

export function PendingReferrals() {
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { pageSize, currentPage, setPage } = usePagination();

  const tenantCtx = useTenantContext();
  const isTenantUsingNewReferralProcess = tenantCtx?.settings.uses_new_referral_process || false;

  const {
    data: paginatedReferrals,
    isFetching,
    isError,
  } = trpcTenant.courseReferral.getAll.useQuery(
    {
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      refetchOnWindowFocus: false,
    },
  );

  let content: React.ReactElement;

  if (isFetching) {
    content = (
      <Box display="flex" justifyContent="center" alignItems="center" h="200px">
        <Spinner size="lg" />
      </Box>
    );
  } else if (isError) {
    content = (
      <Text textAlign="center" color="fg.muted">
        <Trans>Failed to load course referrals. Please try again later.</Trans>
      </Text>
    );
  } else if (paginatedReferrals.pagination.totalCount === 0) {
    content = (
      <Text textAlign="center" color="fg.muted">
        <Trans>No pending referrals found.</Trans>
      </Text>
    );
  } else {
    content = <PendingReferralsTable paginatedReferrals={paginatedReferrals} />;
  }

  return (
    <TableViewLayout
      pageHeader={[
        !isExpanded && (
          <IconButton
            aria-label="expand secondary sidebar"
            icon={<ArrowRightFromLine />}
            variant="tertiary"
            onClick={toggleExpandSubSidebar}
            size="md"
          />
        ),
        <PageHeading key="page-heading">
          <Trans>Pending Referrals</Trans>
        </PageHeading>,
      ]}
      pageActions={isTenantUsingNewReferralProcess ? <CourseReferralButton /> : undefined}
      tableActionsLeft={
        <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
          <Plural
            value={paginatedReferrals.pagination.totalCount || 0}
            zero="0 referrals"
            one="1 referral"
            other="# referrals"
          />
        </Text>
      }
      table={content}
      pagination={
        paginatedReferrals.items.length ? (
          <Pagination pagination={paginatedReferrals.pagination} onPageChange={setPage} />
        ) : null
      }
    />
  );
}
